import React, { memo, useEffect, useState } from "react"
import PropTypes from "prop-types"
import { useNavigate } from "react-router-dom"
import { Button, Label } from "reactstrap"
import { oldRoutes } from "router/old-routes"
import PhotoSelector from "components/form/PhotoSelector"
import Input from "components/form/Input"

import { useTranslation } from "react-i18next"
import { useConfirmModal } from "modules/modals/hooks/useConfirmModal"

// Redux
import { useDispatch } from "react-redux"
import { removeTemplate } from "store/templates"
import { AiEnhancementModal } from "ui/AiEnhancementModal"
import { colors } from "themes/variables"
import { Button as AntdButtton,Tooltip } from "antd"
import { Icon as UIIcon } from "ui/Icon"
TemplateForm.propTypes = {
  form: PropTypes.shape({
    template_type: PropTypes.string.isRequired,
    subject: PropTypes.string,
    heading: PropTypes.string,
    paragraph_text: PropTypes.string,
    button_text: PropTypes.string,
    button_url: PropTypes.string,
    signature: PropTypes.string,
  }).isRequired,
  changeHandler: PropTypes.func.isRequired,
  template: PropTypes.shape({
    category: PropTypes.string.isRequired
  }).isRequired,
  isEdit: PropTypes.bool.isRequired,
}
function TemplateForm({ form, changeHandler, template, isEdit }) {
  const [descriptionValue, setDescriptionValue] = useState("")
  const [profileDescription, setProfileDescription] = useState("")
  const [isShowAiModal, setisShowAiModal] = useState(false)
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const confirmModal = useConfirmModal()

  const withPhoto = ["small_gallery", "large_gallery"].includes(template.category)
  const isEmail = form.template_type === "email"
  const isText = !isEmail
  const isSmallGallery = template.category === "small_gallery"

  const removeTemplateHandler = confirmModal(
    {
      title: "Are you sure?",
      color: "danger",
      submitText: t("global.confirm"),
      cancelText: t("global.cancel")
    },
    () => dispatch(removeTemplate(template.id)).then(() => navigate(oldRoutes.guideMarketingTemplatesPath()))
  )
  useEffect(() => {
    if (form.paragraph_text) { setDescriptionValue(form.paragraph_text) }
  }, [form.paragraph_text])

  useEffect(() => {
    changeHandler({ target: { name: "paragraph_text", value: descriptionValue } });
  }, [descriptionValue]);

  return (
    <div className="vstack gap-30 bg-white p-20 rounded">
      <div className="hstack px-3">
        <Button
          color="white"
          className={[
            "px-30 py-2 fs-7 border-primary-second mx-n3",
            isEmail ? "bg-primary-second z-1 text-white" : "text-primary-second"
          ].join(" ")}
          onClick={(value) => changeHandler(value)}
          value="email"
          name="template_type"
        >
          {t("marketing.labels.email")}
        </Button>
        <Button
          color="white"
          className={[
            "px-30 py-2 fs-7 border-primary-second mx-n3",
            isText ? "bg-primary-second z-1 text-white" : "text-primary-second"
          ].join(" ")}
          onClick={(value) => changeHandler(value)}
          value="text"
          name="template_type"
        >
          {t("marketing.labels.text")}
        </Button>
      </div>
      {isEmail && (
        <div>
          <Label for="subject" className="fs-6">
            {t("marketing.labels.subject")}
          </Label>
          <Input id="subject" type="text" name="subject" value={form.subject || ""} onChange={changeHandler} className="fs-6" withError />
        </div>
      )}
      {template?.category !== "first_basic" && (
        <div>
          <Label for="heading" className="fs-6">
            {t("marketing.labels.heading")}
          </Label>
          <Input id="heading" type="text" name="heading" value={form.heading || ""} onChange={changeHandler} className="fs-6" withError />
        </div>
      )}
      <div>
        <div className="hstack gap-10 justify-content-between mb-2">
          <Label for="paragraph_text" className="fs-6 m-0">
            {t("marketing.labels.paragraph_text")}
          </Label>
          <Tooltip
            title={form?.paragraph_text?.length < 25 ? t('marketing.ai_enable_text_generation') : ""}
            placement="top"
          >
          <AntdButtton
            color="primary"
            variant="solid"
            icon={<UIIcon name="aiEnhancement" color={form?.paragraph_text?.length < 25 ? colors.textPrimary : colors.disabled} />}
            disabled={form?.paragraph_text?.length < 25}
            onClick={() => setisShowAiModal(true)}
          >
            {t("pro.ai_enhancement.button_text")}
          </AntdButtton>
          </Tooltip>
        </div>
        <Input
          id="paragraph_text"
          type="textarea"
          name="paragraph_text"
          rows={10}
          value={form.paragraph_text || ""}
          onChange={changeHandler}
          className="fs-6"
        />
      </div>
      {withPhoto && isEmail && (
        <div>
          <Label className="fs-6">{t("marketing.labels.image", { max: isSmallGallery ? 2 : 4 })}</Label>
          <PhotoSelector imageHeight={104} form={form} changeHandler={changeHandler} max={isSmallGallery ? 2 : 4} />
        </div>
      )}
      {template?.category !== "first_basic" && (
        <>
          {isEmail && (
            <div>
              <Label for="button_text" className="fs-6">
                {t("marketing.labels.button_text")}
              </Label>
              <Input
                id="button_text"
                type="text"
                name="button_text"
                value={form.button_text || ""}
                onChange={changeHandler}
                className="fs-6"
                withError
              />
            </div>
          )}
          <div>
            <Label for="button_url" className="fs-6">
              {isEmail ? t("marketing.labels.button_link") : t("marketing.labels.text_link")}
            </Label>
            <Input
              id="button_url"
              type="text"
              name="button_url"
              value={form.button_url || ""}
              onChange={changeHandler}
              className="fs-6"
              withError
            />
          </div>
        </>
      )}
      <div>
        <Label for="signature" className="fs-6">
          {t("marketing.labels.signature_text")}
        </Label>
        <Input
          id="signature"
          type="text"
          name="signature"
          value={form.signature || ""}
          onChange={changeHandler}
          className="fs-6"
          withError
        />
      </div>
      {isEdit && (
        <Button color="danger" className="fs-7 bg-white text-danger me-auto" onClick={removeTemplateHandler}>
          {t("marketing.remove_template")}
        </Button>
      )}
      {isShowAiModal && (
        <AiEnhancementModal
          open={isShowAiModal}
          onCancel={() => {
            setisShowAiModal(false)
            setProfileDescription('')
          }}
          profileDescription={profileDescription}
          description={descriptionValue}
          setDescription={setDescriptionValue}
          setProfileDescription={setProfileDescription}
          onOk={() => {
            setisShowAiModal(false)
          }}
          text={t("client.marketing_email_content")}
          modalTitle={t("client.marketing_email_enhancer")}
          messages={t("pro.ai_enhancement.form.continue_refinement_info")}
          refinementTooltipTitle={t("pro.ai_enhancement.form.continue_refinement_details")}
          improveWithAITooltipTitle={t("client.imrove_with_ai_tooltip_for_marketing_email")}
        />
      )}
    </div>
  )
}

export default memo(TemplateForm)
